import { html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { register } from 'swiper/element/bundle';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from '../quote-carousel/quote-carousel.styles';

import { DERElement } from '~/common/der-element';
import { Direction } from '~/ui/components/controls/enum';

interface SlidesPerViewConfig {
    mobile?: number;
    tablet?: number;
    desktop?: number;
}

export class QuoteCarousel<T = unknown> extends DERElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: Array }) slides: T[] = [];
    @property({ attribute: false }) renderSlide?: (slide: T, index: number) => unknown;

    @property({ type: Object }) slidesPerView: SlidesPerViewConfig = {
        mobile: 1.125,
        tablet: 2.125,
        desktop: 3.125,
    };

    @state()
    private isMobile = false;

    @state()
    private isBeginning = true;

    @state()
    private isEnd = false;

    private swiperEl: HTMLElement | null = null;

    override firstUpdated() {
        void this._registerSwiper();
    }

    private async _registerSwiper() {
        this.swiperEl = this.shadowRoot?.querySelector('swiper-container') as HTMLElement | null;
        if (!this.swiperEl || !!('swiper' in this.swiperEl)) {
            return;
        }
        register();
        if (this.swiperEl) {
            Object.assign(this.swiperEl, {
                spaceBetween: 16,
                navigation: {
                    nextEl: '.custom-next',
                    prevEl: '.custom-prev',
                },
                breakpoints: {
                    0: {
                        slidesPerView: this.slidesPerView.mobile ?? 1.125,
                    },
                    768: {
                        slidesPerView: this.slidesPerView.tablet ?? 2.125,
                    },
                    1024: {
                        slidesPerView: this.slidesPerView.desktop ?? 3.125,
                    },
                },
                on: {
                    init: () => this.updateButtonStates(),
                    slideChange: () => this.updateButtonStates(),
                    breakpoint: (swiper: {
                        currentBreakpoint: string;
                        originalParams: { breakpoints?: Record<string, { slidesPerView?: number }> };
                    }) => {
                        const currentBreakpoint = swiper.currentBreakpoint;
                        const breakpointSettings = swiper.originalParams.breakpoints?.[currentBreakpoint];

                        const currentSlidesPerView = breakpointSettings?.slidesPerView ?? 1;

                        this.isMobile = currentSlidesPerView < 3;
                    },
                },
            });

            await new Promise((resolve) => requestAnimationFrame(resolve));
            (this.swiperEl as unknown as { initialize: () => void }).initialize();
        }
    }

    private updateButtonStates() {
        if (!this.swiperEl) return;

        this.isBeginning =
            (
                this.swiperEl as HTMLElement & {
                    swiper?: { isBeginning: boolean };
                }
            ).swiper?.isBeginning ?? true;
        this.isEnd = (this.swiperEl as HTMLElement & { swiper?: { isEnd: boolean } }).swiper?.isEnd ?? false;

        this.requestUpdate();
    }

    renderButtons() {
        return html`
            <div
                slot=${!this.isMobile ? 'container-start' : ''}
                class=${this.slides.length < 4 ? 'o-card-carousel__desktop-placeholder' : ''}
            ></div>
            <div
                slot=${!this.isMobile ? 'container-end' : 'container-start'}
                class="o-card-carousel__quote-placeholder"
            ></div>
            <div
                slot=${this.isMobile ? 'container-end' : 'container-start'}
                class="o-card-carousel__swiper-buttons-container ${this.slides.length === 3
                    ? 'equal-3'
                    : this.slides.length === 2
                      ? 'equal-2'
                      : this.slides.length === 1
                        ? 'equal-1'
                        : ''}"
            >
                <der-ui-controls
                    direction=${Direction.Left}
                    class="custom-prev"
                    aria-label="Previous slide"
                    ?disabled=${this.isBeginning}
                ></der-ui-controls>
                <der-ui-controls
                    direction=${Direction.Right}
                    class="custom-next"
                    aria-label="Next slide"
                    ?disabled=${this.isEnd}
                ></der-ui-controls>
            </div>
        `;
    }

    override render() {
        return html`
            <swiper-container class="o-card-carousel" .loop=${false} .init=${false}>
                ${this.renderButtons()}
                ${this.slides.map(
                    (slide, index) => html`<swiper-slide>${this.renderSlide?.(slide, index)}</swiper-slide>`
                )}
            </swiper-container>
        `;
    }
}
